import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { CANDIDATE_STATUS_VALUES, useCandidateStatus } from "@/utils/candidate/statusUtils";

interface CandidateMassUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCandidateIds: string[];
  onUpdate: () => void;
}

// Convert candidate status values to options format (exclude 'new' - mass update should never set candidates back to new)
const getCandidateStatusOptions = (getLocalizedStatus: (status: string) => string) => {
  return Object.values(CANDIDATE_STATUS_VALUES)
    .filter(value => value !== CANDIDATE_STATUS_VALUES.NEW)
    .map(value => ({
      value,
      label: getLocalizedStatus(value)
    }));
};

export const CandidateMassUpdateModal = ({ 
  isOpen, 
  onClose, 
  selectedCandidateIds, 
  onUpdate 
}: CandidateMassUpdateModalProps) => {
  const { t } = useTranslation();
  const { getLocalizedStatus } = useCandidateStatus();
  const [fieldValue, setFieldValue] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFieldValue("");
    }
  }, [isOpen]);

  const handleUpdate = async () => {
    if (fieldValue === "" || selectedCandidateIds.length === 0) {
      toast({
        title: "Error",
        description: "Please select a status",
        variant: "destructive"
      });
      return;
    }

    setIsUpdating(true);

    try {
      // Prepare the update data
      const updateData = { status: fieldValue };

      // Perform batch update
      const { error } = await supabase
        .from('candidates')
        .update(updateData)
        .in('id', selectedCandidateIds);

      if (error) throw error;

      // When candidate becomes hired or unavailable, close all open matches
      if (fieldValue === 'hired' || fieldValue === 'unavailable')
        for (const candidateId of selectedCandidateIds)
          await supabase.rpc('set_match_candidate_not_available', { p_candidate_id: candidateId });

      // Create notifications for status changes
      try {
        // Get candidate details for notifications
        const { data: candidatesData } = await supabase
          .from('candidates')
          .select(`
            id,
            name,
            email
          `)
          .in('id', selectedCandidateIds);

        if (candidatesData) {
          // Create notifications for each candidate
          for (const candidate of candidatesData) {
            try {
              await supabase.rpc('create_org_notifications_with_subscriptions', {
                p_organization_uuid: null, // Will use current user's organization
                p_type: 'candidate_status_change',
                p_title: `Bulk Status Update: ${candidate.name} → ${getLocalizedStatus(fieldValue)}`,
                p_message: `${candidate.name} has been moved to "${getLocalizedStatus(fieldValue)}" via bulk update.`,
                p_data: {
                  entity_type: 'candidate',
                  entity_id: candidate.id,
                  candidate_name: candidate.name,
                  candidate_email: candidate.email,
                  new_status: fieldValue,
                  action: 'bulk_status_update'
                },
                p_entity_type: 'candidate',
                p_entity_id: candidate.id
              });
            } catch (notificationError) {
              console.warn('Failed to create notification for candidate:', candidate.id, notificationError);
            }
          }
        }
      } catch (notificationError) {
        console.warn('Failed to create notifications for bulk update:', notificationError);
      }

      toast({
        title: "Success",
        description: `Successfully updated ${selectedCandidateIds.length} candidate(s)`,
      });

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating candidates:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update candidates",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const statusOptions = getCandidateStatusOptions(getLocalizedStatus);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Mass Update</DialogTitle>
          <DialogDescription>
            Update fields for {selectedCandidateIds.length} selected candidate(s)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="field-select">Field to Update</Label>
            <Select value="status" disabled>
              <SelectTrigger id="field-select" className="bg-gray-50">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="status">Status</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="status-select">New Value</Label>
            <Select value={fieldValue} onValueChange={setFieldValue}>
              <SelectTrigger id="status-select">
                <SelectValue placeholder="Select new status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdate} 
            disabled={fieldValue === "" || isUpdating}
          >
            {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update {selectedCandidateIds.length} Candidate(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
